import { addRecord, updateRecord } from './firestoreLive';
import { reelReport } from './reelTelemetry';

/**
 * First-party visitor analytics.
 *
 * Every field below is dictated by the `isValidTrafficEvent()` rule in
 * firestore.rules, which uses hasOnly() plus a per-field type check: send an
 * extra key, omit one, or break a length bound and the whole write is rejected.
 * Keep this file and the rules in step.
 */

const VISITOR_KEY = 'hh-visitor-id';
const SESSION_KEY = 'hh-session-id';
const GEO_KEY = 'hh-geo';
const OWNER_KEY = 'hh-owner';

const rid = () =>
  Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);

/** Longer than the 5-char floor the rules require, shorter than the 64 ceiling. */
const stableId = (store: Storage, key: string) => {
  try {
    const found = store.getItem(key);
    if (found && found.length > 5) return found;
    const made = rid().slice(0, 32);
    store.setItem(key, made);
    return made;
  } catch {
    // private mode or blocked storage: still record the hit, just unlinked
    return rid().slice(0, 32);
  }
};

const clamp = (n: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, Number.isFinite(n) ? Math.round(n) : 0));

const cut = (s: unknown, max: number) => String(s ?? '').slice(0, max);

const deviceType = (): 'desktop' | 'mobile' | 'tablet' => {
  const ua = navigator.userAgent;
  if (/iPad|Tablet|PlayBook|Silk/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua)))
    return 'tablet';
  if (/Mobi|Android|iPhone|iPod|Windows Phone/i.test(ua)) return 'mobile';
  return 'desktop';
};

const browserName = () => {
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return 'Edge';
  if (/OPR\/|Opera/.test(ua)) return 'Opera';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Safari\//.test(ua)) return 'Safari';
  return 'Other';
};

const osName = () => {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac OS X|Macintosh/.test(ua)) return 'macOS';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Other';
};

/** Country, city and the network's owner only: the IP itself is never stored.
 *  It is personal data under GDPR, it added nothing these fields don't already
 *  give, and keeping it would have required a privacy policy the site doesn't
 *  have. The network is the organisation that owns the connection — a studio
 *  on its office network, otherwise just the ISP — never the person.
 *
 *  The rules demand a non-empty countryCode and countryName, so every failure
 *  path still has to produce something. ipwho.is is keyless and sets no
 *  cookies, which matters: the site is deliberately cookie-free. */
type Geo = { countryCode: string; countryName: string; city: string; region: string; network: string };
const UNKNOWN_GEO: Geo = { countryCode: 'XX', countryName: 'Unknown', city: '', region: '', network: '' };

const lookupGeo = async (): Promise<Geo> => {
  try {
    const cached = sessionStorage.getItem(GEO_KEY);
    if (cached) return JSON.parse(cached) as Geo;
  } catch {
    /* storage blocked; just refetch */
  }
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 4000);
    const res = await fetch('https://ipwho.is/', { signal: ctrl.signal });
    clearTimeout(timer);
    const d = await res.json();
    if (!d?.success) return UNKNOWN_GEO;
    const geo: Geo = {
      countryCode: cut(d.country_code, 4) || 'XX',
      countryName: cut(d.country, 64) || 'Unknown',
      city: cut(d.city, 64),
      region: cut(d.region, 64),
      network: cut(d.connection?.org || d.connection?.isp, 80),
    };
    try {
      sessionStorage.setItem(GEO_KEY, JSON.stringify(geo));
    } catch {
      /* not worth failing the event over */
    }
    return geo;
  } catch {
    return UNKNOWN_GEO;
  }
};

const pageLoadMs = () => {
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  if (!nav) return 0;
  const ms = nav.loadEventEnd > 0 ? nav.loadEventEnd : nav.domContentLoadedEventEnd;
  return clamp(ms, 0, 120000);
};

/**
 * A local run is not a visitor. Every `npm run dev` and every Playwright run
 * was writing into the collections the dashboard reads, and with reel health
 * and project clicks now on those records the noise lands directly on the
 * signal: a headless browser reporting 'ok' dilutes exactly the ratio worth
 * watching, and a test clicking a card invents demand for it.
 */
const isLocal = () => {
  const h = location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h.endsWith('.local');
};

/**
 * Hamlet is not a visitor either. Any browser that has opened /admin is his,
 * so it stops recording from then on — including the 'View site' link out of
 * the panel, which used to land as a fresh visit every time. The dashboard
 * also hides the visits this browser made before it was marked, by its id.
 */
const isOwner = () => {
  try {
    return localStorage.getItem(OWNER_KEY) === '1';
  } catch {
    return false;
  }
};

export const markOwner = () => {
  try {
    localStorage.setItem(OWNER_KEY, '1');
  } catch {
    /* blocked storage: nothing to remember it in */
  }
};

/** this browser's visitor id, without minting one if it has none */
export const ownVisitorId = () => {
  try {
    return localStorage.getItem(VISITOR_KEY) || '';
  } catch {
    return '';
  }
};

/**
 * Crawlers and link-preview fetchers that run real JavaScript. Most announce
 * themselves in the user agent; automated Chrome sets navigator.webdriver.
 * What slips past this is caught on read, by network, in the dashboard.
 */
const isBot = () =>
  navigator.webdriver === true ||
  /bot|crawl|spider|slurp|headless|lighthouse|preview|facebookexternalhit|embedly|quora link/i.test(
    navigator.userAgent
  );

/**
 * Core Web Vitals, the three Google ranks on: how long the main content took
 * to appear, how long a tap took to answer, and how much the layout jumped.
 * reportAllChanges keeps these current, so whatever they are when the visit
 * ends is what rides the engagement write. Loaded lazily: the library is
 * small, but nothing measuring the page should weigh on it.
 */
const vitals: { lcpMs?: number; inpMs?: number; cls?: number } = {};
const watchVitals = () => {
  import('web-vitals')
    .then(({ onLCP, onINP, onCLS }) => {
      const opts = { reportAllChanges: true };
      onLCP((m) => (vitals.lcpMs = clamp(m.value, 0, 120000)), opts);
      onINP((m) => (vitals.inpMs = clamp(m.value, 0, 60000)), opts);
      // CLS is unitless and small; three decimals is all it has to say
      onCLS((m) => (vitals.cls = Math.min(10, Math.round(m.value * 1000) / 1000)), opts);
    })
    .catch(() => {
      /* no vitals is fine */
    });
};

let started = false;

/** Records one page view, then keeps its engagement figures up to date. */
export const startAnalytics = () => {
  if (started) return;

  if (isLocal() || isOwner() || isBot()) return;

  started = true;
  watchVitals();

  const visitorId = stableId(localStorage, VISITOR_KEY);
  const sessionId = stableId(sessionStorage, SESSION_KEY);

  let maxScrollDepth = 0;
  const openedAt = Date.now();
  const onScroll = () => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    const pct = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 0;
    if (pct > maxScrollDepth) maxScrollDepth = Math.min(100, pct);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  const record = async () => {
    const geo = await lookupGeo();
    const params = new URLSearchParams(location.search);
    let referrerHost = '';
    try {
      referrerHost = document.referrer ? new URL(document.referrer).host : '';
    } catch {
      referrerHost = '';
    }
    if (referrerHost === location.host) referrerHost = '';

    const conn = (navigator as { connection?: { effectiveType?: string } }).connection;

    try {
      const id = await addRecord('traffic_events', {
        visitorId,
        sessionId,
        path: cut(location.pathname || '/', 120) || '/',
        referrerHost,
        countryCode: geo.countryCode,
        countryName: geo.countryName,
        city: geo.city,
        region: geo.region,
        // sessions cached before this field existed carry no network
        network: geo.network ?? '',
        deviceType: deviceType(),
        browser: cut(browserName(), 32),
        os: cut(osName(), 32),
        screenWidth: clamp(screen.width, 0, 8192),
        screenHeight: clamp(screen.height, 0, 8192),
        viewportWidth: clamp(window.innerWidth, 0, 8192),
        viewportHeight: clamp(window.innerHeight, 0, 8192),
        pixelRatio: clamp(window.devicePixelRatio, 0, 10),
        colorDepth: clamp(screen.colorDepth, 0, 32),
        // the rules cap this at 8 characters
        connectionType: cut(conn?.effectiveType || 'unknown', 8),
        pageLoadMs: pageLoadMs(),
        timezone: cut(Intl.DateTimeFormat().resolvedOptions().timeZone, 64),
        locale: cut(navigator.language, 24),
        utmSource: cut(params.get('utm_source'), 80),
        utmMedium: cut(params.get('utm_medium'), 80),
        utmCampaign: cut(params.get('utm_campaign'), 120),
      });
      // the write was rejected or the SDK never arrived: nothing to update
      if (!id) return;

      // Engagement lands as an update because duration and depth are only known
      // once the visitor leaves. The rules allow exactly these two keys.
      let flushed = false;
      const flush = () => {
        if (flushed) return;
        flushed = true;
        void updateRecord('traffic_events', id, {
          durationSec: clamp((Date.now() - openedAt) / 1000, 0, 86400),
          maxScrollDepth: clamp(maxScrollDepth, 0, 100),
          // Whether the reel actually played, decided only now that the visit is
          // over. isValidEngagementUpdate permits these four alongside the two
          // above and only on this one write, so they ride the record that
          // already exists rather than opening a new collection.
          ...reelReport(),
          // only the ones actually measured; the rules take each or its absence
          ...vitals,
        });
      };
      addEventListener('pagehide', flush);
      addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flush();
      });
    } catch {
      // Analytics must never take the site down with it.
    }
  };

  // Never compete with the first paint or the reel for bandwidth.
  const idle = (window as { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback;
  if (idle) idle(() => void record());
  else setTimeout(() => void record(), 2500);
};

export type Interaction =
  | 'cv_view'
  | 'cv_download'
  | 'email_copy'
  | 'phone_copy'
  | 'project_click'
  | 'social_click';

/**
 * The things a visitor does that mean anything, per the visitor_interactions
 * rule. `label` says WHICH — which project was opened, which social link was
 * followed — and is the difference between knowing six cards were clicked and
 * knowing which one of them people actually want.
 */
export const trackInteraction = (action: Interaction, label?: string) => {
  // Same rule as the page view above. It was missing here, which meant local
  // clicks were still being counted as real ones.
  if (isLocal() || isOwner() || isBot()) return;
  // The ids are read synchronously, before the SDK is awaited: this fires from
  // a click handler, and the visitor is often navigating away on the same tick.
  try {
    const trimmed = cut(label, 64);
    void addRecord('visitor_interactions', {
      visitorId: stableId(localStorage, VISITOR_KEY),
      sessionId: stableId(sessionStorage, SESSION_KEY),
      action,
      // omitted rather than sent empty: the rule accepts the key or its absence,
      // not a blank string
      ...(trimmed ? { label: trimmed } : {}),
    });
  } catch {
    /* ignore */
  }
};
